import { ClassItem, SubmissionItem } from '../types';
import { api } from '../services/api';
import { openPreviewModal } from '../components/PreviewModal';
import { showToast } from '../utils/toast';

export async function renderLibraryView(
  container: HTMLElement,
  options: {
    isPublicOnly?: boolean;
    currentClass?: ClassItem;
    onNavigate: (path: string) => void;
  }
): Promise<void> {
  const { isPublicOnly = false, currentClass, onNavigate } = options;

  let submissions: SubmissionItem[] = await api.getSubmissions(
    isPublicOnly ? undefined : currentClass?.id,
    isPublicOnly
  );

  let activeGenre: string = 'all';
  let searchQuery: string = '';
  let activeSort: 'newest' | 'oldest' | 'title' = 'newest';

  const renderContent = () => {
    // フィルタリング & ソート
    let filtered = submissions.filter((item) => {
      if (activeGenre !== 'all' && item.genre !== activeGenre) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matchTitle = item.title.toLowerCase().includes(q);
        const matchAuthor = item.author.toLowerCase().includes(q);
        const matchDesc = item.description.toLowerCase().includes(q);
        if (!matchTitle && !matchAuthor && !matchDesc) return false;
      }
      return true;
    });

    if (activeSort === 'newest') {
      filtered.sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());
    } else if (activeSort === 'oldest') {
      filtered.sort((a, b) => new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime());
    } else if (activeSort === 'title') {
      filtered.sort((a, b) => a.title.localeCompare(b.title, 'ja'));
    }

    container.innerHTML = `
      ${isPublicOnly ? `
        <!-- 公開ライブラリ用 ヒーローバナー -->
        <section class="hero-banner theme-blue" aria-label="公開ライブラリ情報">
          <h1 class="banner-title">🌐 公開ライブラリ（Showcase）</h1>
          <p class="banner-desc">各授業から受講生が権利同意を経て「完全公開」した優秀なWeb作品・アプリが集まるギャラリーです。</p>
          <div class="banner-meta">
            <div class="banner-pill">
              <span>全 ${submissions.length} 件の公開作品</span>
            </div>
          </div>
        </section>
      ` : currentClass ? `
        <!-- 授業用 ヒーローバナー -->
        <section class="hero-banner theme-${currentClass.themeColor}" aria-label="授業情報">
          <h1 class="banner-title">${currentClass.name}</h1>
          <p class="banner-desc">${currentClass.description || '提出されたHTML作品の鑑賞・ピアレビューギャラリー'}</p>
          <div class="banner-meta">
            <div class="banner-pill" id="btn-copy-lib-url" title="ライブラリURLをコピー">
              <span>📋 ライブラリURLをコピー</span>
            </div>
            <div class="banner-pill">
              <span>提出作品: ${submissions.length} 件</span>
            </div>
          </div>
        </section>

        <!-- Classroom風 タブバー -->
        <nav class="tab-bar" aria-label="ナビゲーション">
          <button class="tab-btn" id="tab-submit" type="button">
            <span>📤 提出箱</span>
          </button>
          <button class="tab-btn active" id="tab-library" type="button">
            <span>📚 授業用ライブラリ</span>
          </button>
        </nav>
      ` : ''}

      <!-- ツールバー（検索・フィルタ・ソート） -->
      <div class="library-toolbar">
        <div class="filter-chips" role="tablist" aria-label="ジャンルフィルター">
          <button class="chip ${activeGenre === 'all' ? 'active' : ''}" data-genre="all">すべて</button>
          <button class="chip ${activeGenre === 'ゲーム' ? 'active' : ''}" data-genre="ゲーム">🎮 ゲーム</button>
          <button class="chip ${activeGenre === 'ツール' ? 'active' : ''}" data-genre="ツール">🛠️ ツール</button>
          <button class="chip ${activeGenre === 'アニメーション' ? 'active' : ''}" data-genre="アニメーション">✨ アニメ</button>
          <button class="chip ${activeGenre === 'ポートフォリオ' ? 'active' : ''}" data-genre="ポートフォリオ">💼 ポートフォリオ</button>
          <button class="chip ${activeGenre === 'アート' ? 'active' : ''}" data-genre="アート">🎨 アート</button>
        </div>

        <div style="display: flex; align-items: center; gap: 12px; flex-wrap: wrap;">
          <!-- 検索ボックス -->
          <div class="search-box">
            <span>🔍</span>
            <input
              type="text"
              id="search-input"
              class="search-input"
              placeholder="タイトル・作者名で検索..."
              value="${searchQuery}"
            />
          </div>

          <!-- ソートセレクタ -->
          <select id="sort-select" class="form-select" style="padding: 6px 12px; font-size: 13px;">
            <option value="newest" ${activeSort === 'newest' ? 'selected' : ''}>新着順</option>
            <option value="oldest" ${activeSort === 'oldest' ? 'selected' : ''}>古い順</option>
            <option value="title" ${activeSort === 'title' ? 'selected' : ''}>タイトル順</option>
          </select>
        </div>
      </div>

      <!-- 作品カードグリッド -->
      ${filtered.length === 0 ? `
        <div style="text-align: center; padding: 60px 20px; background: var(--color-surface); border-radius: 8px; border: 1px solid var(--color-border);">
          <div style="font-size: 48px; margin-bottom: 12px;">📭</div>
          <h3 style="font-size: 16px; font-weight: 500; color: var(--color-text-primary); margin-bottom: 6px;">
            該当する作品が見つかりませんでした
          </h3>
          <p style="font-size: 13px; color: var(--color-text-secondary); margin-bottom: 16px;">
            検索条件を変更するか、新しい作品を提出してください。
          </p>
          ${!isPublicOnly && currentClass ? `
            <button class="btn btn-primary" id="btn-goto-submit" type="button">
              📤 作品を提出する
            </button>
          ` : ''}
        </div>
      ` : `
        <div class="card-grid" role="list">
          ${filtered.map((item, idx) => `
            <article class="sub-card" data-idx="${idx}" role="listitem" tabindex="0" title="${escapeHtml(item.title)} をプレビュー">
              <div class="card-thumb-wrap">
                <img src="${item.thumbnailUrl}" alt="${escapeHtml(item.title)}" class="card-thumb-img" loading="lazy" />
                <span class="card-badge ${item.visibility === 'public' ? 'badge-public' : 'badge-private'}">
                  ${item.visibility === 'public' ? '🌐 完全公開' : '🔒 限定公開'}
                </span>
              </div>
              <div class="card-body">
                <h3 class="card-title">${escapeHtml(item.title)}</h3>
                <p class="card-desc">${escapeHtml(item.description || '（説明文はありません）')}</p>
                <div class="card-footer">
                  <div class="card-author">
                    <div class="avatar-mini">${escapeHtml(item.author.charAt(0) || '匿')}</div>
                    <span>${escapeHtml(item.author)}</span>
                  </div>
                  <span>${new Date(item.submittedAt).toLocaleDateString('ja-JP')}</span>
                </div>
              </div>
            </article>
          `).join('')}
        </div>
      `}
    `;

    // イベントバインド
    if (!isPublicOnly && currentClass) {
      container.querySelector('#tab-submit')?.addEventListener('click', () => {
        onNavigate(`/c/${currentClass.id}/submit`);
      });
      container.querySelector('#btn-goto-submit')?.addEventListener('click', () => {
        onNavigate(`/c/${currentClass.id}/submit`);
      });
      container.querySelector('#btn-copy-lib-url')?.addEventListener('click', () => {
        navigator.clipboard.writeText(window.location.href);
        showToast('授業用ライブラリのURLをコピーしました！', 'success');
      });
    }

    // ジャンルチップクリック
    container.querySelectorAll('.chip').forEach((chip) => {
      chip.addEventListener('click', () => {
        activeGenre = chip.getAttribute('data-genre') || 'all';
        renderContent();
      });
    });

    // 検索入力
    const searchInput = container.querySelector('#search-input') as HTMLInputElement;
    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        searchQuery = (e.target as HTMLInputElement).value;
        renderFilteredOnly();
      });
    }

    // ソート変更
    const sortSelect = container.querySelector('#sort-select') as HTMLSelectElement;
    if (sortSelect) {
      sortSelect.addEventListener('change', (e) => {
        activeSort = (e.target as HTMLSelectElement).value as any;
        renderContent();
      });
    }

    // カードクリックでプレビューモーダル
    container.querySelectorAll('.sub-card').forEach((card) => {
      card.addEventListener('click', () => {
        const idx = parseInt(card.getAttribute('data-idx') || '0', 10);
        const item = filtered[idx];
        if (item) {
          openPreviewModal(item);
        }
      });
    });
  };

  const renderFilteredOnly = () => {
    // 検索の入力フォーカスが外れないよう再描画を最適化
    renderContent();
    const searchInput = container.querySelector('#search-input') as HTMLInputElement;
    if (searchInput) {
      searchInput.focus();
      searchInput.setSelectionRange(searchQuery.length, searchQuery.length);
    }
  };

  renderContent();
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
